// Nomenclature React Hooks
// Custom hooks for fetching and managing nomenclatures

import { useState, useEffect, useCallback } from 'react';
import {
  protocolCategoryService,
  protocolTypeService,
  roomTypeService,
  ppeTypeService,
  verificationMethodService,
  triggerEventService,
  frequencyTypeService,
  locationTypeService,
  actionTypeService,
  equipmentService,
  supplyService,
  conditionTypeService,
  documentationFieldTypeService,
  riskTypeService,
  processCategoryService
} from '../services/nomenclatureService';

/**
 * Generic hook for nomenclatures
 */
function useNomenclatureBase(service, activeOnly = true) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const data = await service.getAll(activeOnly);
      setItems(data);
      setError(null);
    } catch (err) {
      setError(err);
      console.error('Error fetching nomenclature:', err);
    } finally {
      setLoading(false);
    }
  }, [service, activeOnly]);
  
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);
  
  const refresh = useCallback(() => {
    fetchItems();
  }, [fetchItems]);
  
  return { items, loading, error, refresh };
}

/**
 * Hook for protocol categories
 */
export function useProtocolCategories(activeOnly = true) {
  return useNomenclatureBase(protocolCategoryService, activeOnly);
}

/**
 * Hook for protocol types
 */
export function useProtocolTypes(activeOnly = true) {
  return useNomenclatureBase(protocolTypeService, activeOnly);
}

/**
 * Hook for room types
 */
export function useRoomTypes(activeOnly = true) {
  return useNomenclatureBase(roomTypeService, activeOnly);
}

/**
 * Hook for PPE types
 */
export function usePPETypes(activeOnly = true) {
  return useNomenclatureBase(ppeTypeService, activeOnly);
}

/**
 * Hook for verification methods
 */
export function useVerificationMethods(activeOnly = true) {
  return useNomenclatureBase(verificationMethodService, activeOnly);
}

/**
 * Hook for trigger events
 */
export function useTriggerEvents(activeOnly = true) {
  return useNomenclatureBase(triggerEventService, activeOnly);
}

/**
 * Hook for frequency types
 */
export function useFrequencyTypes(activeOnly = true) {
  return useNomenclatureBase(frequencyTypeService, activeOnly);
}

/**
 * Hook for location types
 */
export function useLocationTypes(activeOnly = true) {
  return useNomenclatureBase(locationTypeService, activeOnly);
}

/**
 * Hook for action types
 */
export function useActionTypes(activeOnly = true) {
  return useNomenclatureBase(actionTypeService, activeOnly);
}

/**
 * Hook for equipment
 */
export function useEquipment(activeOnly = true) {
  return useNomenclatureBase(equipmentService, activeOnly);
}

/**
 * Hook for supplies
 */
export function useSupplies(activeOnly = true) {
  return useNomenclatureBase(supplyService, activeOnly);
}

/**
 * Hook for condition types
 */
export function useConditionTypes(activeOnly = true) {
  return useNomenclatureBase(conditionTypeService, activeOnly);
}

/**
 * Hook for documentation field types
 */
export function useDocumentationFieldTypes(activeOnly = true) {
  return useNomenclatureBase(documentationFieldTypeService, activeOnly);
}

/**
 * Hook for risk types
 */
export function useRiskTypes(activeOnly = true) {
  return useNomenclatureBase(riskTypeService, activeOnly);
}

/**
 * Hook for process categories
 */
export function useProcessCategories(activeOnly = true) {
  return useNomenclatureBase(processCategoryService, activeOnly);
}

/**
 * Hook for all nomenclatures (fetch all at once)
 */
export function useAllNomenclatures(activeOnly = true) {
  const protocolCategories = useProtocolCategories(activeOnly);
  const protocolTypes = useProtocolTypes(activeOnly);
  const roomTypes = useRoomTypes(activeOnly);
  const ppeTypes = usePPETypes(activeOnly);
  const verificationMethods = useVerificationMethods(activeOnly);
  const triggerEvents = useTriggerEvents(activeOnly);
  const frequencyTypes = useFrequencyTypes(activeOnly);
  const locationTypes = useLocationTypes(activeOnly);
  const actionTypes = useActionTypes(activeOnly);
  const equipment = useEquipment(activeOnly);
  const supplies = useSupplies(activeOnly);
  const conditionTypes = useConditionTypes(activeOnly);
  const documentationFieldTypes = useDocumentationFieldTypes(activeOnly);
  const riskTypes = useRiskTypes(activeOnly);
  const processCategories = useProcessCategories(activeOnly);
  
  const loading = 
    protocolCategories.loading ||
    protocolTypes.loading ||
    roomTypes.loading ||
    ppeTypes.loading ||
    verificationMethods.loading ||
    triggerEvents.loading ||
    frequencyTypes.loading ||
    locationTypes.loading ||
    actionTypes.loading ||
    equipment.loading ||
    supplies.loading ||
    conditionTypes.loading ||
    documentationFieldTypes.loading ||
    riskTypes.loading ||
    processCategories.loading;
  
  const error = 
    protocolCategories.error ||
    protocolTypes.error ||
    roomTypes.error ||
    ppeTypes.error ||
    verificationMethods.error ||
    triggerEvents.error ||
    frequencyTypes.error ||
    locationTypes.error ||
    actionTypes.error ||
    equipment.error ||
    supplies.error ||
    conditionTypes.error ||
    documentationFieldTypes.error ||
    riskTypes.error ||
    processCategories.error;
  
  const refresh = () => {
    protocolCategories.refresh();
    protocolTypes.refresh();
    roomTypes.refresh();
    ppeTypes.refresh();
    verificationMethods.refresh();
    triggerEvents.refresh();
    frequencyTypes.refresh();
    locationTypes.refresh();
    actionTypes.refresh();
    equipment.refresh();
    supplies.refresh();
    conditionTypes.refresh();
    documentationFieldTypes.refresh();
    riskTypes.refresh();
    processCategories.refresh();
  };
  
  return {
    protocolCategories: protocolCategories.items,
    protocolTypes: protocolTypes.items,
    roomTypes: roomTypes.items,
    ppeTypes: ppeTypes.items,
    verificationMethods: verificationMethods.items,
    triggerEvents: triggerEvents.items,
    frequencyTypes: frequencyTypes.items,
    locationTypes: locationTypes.items,
    actionTypes: actionTypes.items,
    equipment: equipment.items,
    supplies: supplies.items,
    conditionTypes: conditionTypes.items,
    documentationFieldTypes: documentationFieldTypes.items,
    riskTypes: riskTypes.items,
    processCategories: processCategories.items,
    loading,
    error,
    refresh
  };
}

/**
 * Hook for nomenclature mutations
 */
export function useNomenclatureMutations(service) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const create = async (data, userId) => {
    try {
      setLoading(true);
      setError(null);
      const id = await service.create(data, userId);
      return id;
    } catch (err) {
      setError(err);
      console.error('Error creating nomenclature:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const update = async (id, updates, userId) => {
    try {
      setLoading(true);
      setError(null);
      await service.update(id, updates, userId);
    } catch (err) {
      setError(err);
      console.error('Error updating nomenclature:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const remove = async (id, userId) => {
    try {
      setLoading(true);
      setError(null);
      await service.delete(id, userId);
    } catch (err) {
      setError(err);
      console.error('Error deleting nomenclature:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const reorder = async (items, userId) => {
    try {
      setLoading(true);
      setError(null);
      await service.reorder(items, userId);
    } catch (err) {
      setError(err);
      console.error('Error reordering nomenclature:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  return {
    create,
    update,
    remove,
    reorder,
    loading,
    error
  };
}

/**
 * Specific mutation hooks for each nomenclature type
 */
export function useProtocolCategoryMutations() {
  return useNomenclatureMutations(protocolCategoryService);
}

export function useProtocolTypeMutations() {
  return useNomenclatureMutations(protocolTypeService);
}

export function useRoomTypeMutations() {
  return useNomenclatureMutations(roomTypeService);
}

export function usePPETypeMutations() {
  return useNomenclatureMutations(ppeTypeService);
}

export function useVerificationMethodMutations() {
  return useNomenclatureMutations(verificationMethodService);
}

export function useTriggerEventMutations() {
  return useNomenclatureMutations(triggerEventService);
}

export function useFrequencyTypeMutations() {
  return useNomenclatureMutations(frequencyTypeService);
}

export function useLocationTypeMutations() {
  return useNomenclatureMutations(locationTypeService);
}

export function useActionTypeMutations() {
  return useNomenclatureMutations(actionTypeService);
}

export function useEquipmentMutations() {
  return useNomenclatureMutations(equipmentService);
}

export function useSupplyMutations() {
  return useNomenclatureMutations(supplyService);
}

export function useConditionTypeMutations() {
  return useNomenclatureMutations(conditionTypeService);
}

export function useDocumentationFieldTypeMutations() {
  return useNomenclatureMutations(documentationFieldTypeService);
}

export function useRiskTypeMutations() {
  return useNomenclatureMutations(riskTypeService);
}

export function useProcessCategoryMutations() {
  return useNomenclatureMutations(processCategoryService);
}

